/**
 * The files attached to a row.
 *
 * The second editor that cannot live in a cell: a list of what is attached and
 * a control for adding more is taller than a row, so the field is marked as
 * needing room and the grid answers a double-click on one of its cells by
 * opening the row.
 *
 * **The draft carries the whole files, as JSON**, for the reason a link row's
 * does: what is chosen has to survive until it is sent. Here it carries more
 * than is sent -- the url and the thumbnail the editor draws with, which the
 * backend fills in again from the name. What goes to the server is the name and
 * the name to show it under, which are the only two it reads.
 */
import { useRef, useState } from 'react'
import { Paperclip, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/shared/lib/utils'
import { fileLabel, fileThumbnail, parseFiles } from '../../files'
import type { CellEditorProps } from '.'
import { useEditorSources } from './sources'

export function FileInput({
  value,
  onValueChange,
  onKeyDown,
  label,
  autoFocus,
}: CellEditorProps) {
  const { t } = useTranslation()
  const { uploadFile } = useEditorSources()
  const files = parseFiles(value)
  const picker = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(0)
  const [failed, setFailed] = useState(false)

  async function attach(chosen: readonly File[]): Promise<void> {
    setFailed(false)
    setUploading((at) => at + chosen.length)

    // Accumulated here rather than read back from the draft between uploads.
    // Each file is written as it lands, and the render that writes it has not
    // happened by the time the next one resolves -- so anything read from the
    // component would be a file behind, and the second upload would land on
    // top of the first.
    let held = files

    // One at a time rather than all at once. A row rarely gets more than a
    // handful, they appear as they land, and a failure part-way names the file
    // that failed instead of leaving it unclear which of five did.
    for (const file of chosen) {
      try {
        held = [...held, await uploadFile(file)]
        onValueChange(JSON.stringify(held))
      } catch {
        setFailed(true)
      } finally {
        setUploading((at) => at - 1)
      }
    }
  }

  return (
    <div className="flex flex-col gap-2" onKeyDown={onKeyDown}>
      {files.length > 0 && (
        <ul className="flex flex-col gap-1" aria-label={t('file.attached')}>
          {files.map((file) => {
            // The forty-eight pixel one, where the cell asks for the
            // twenty-one: there is room for it here, and the cell's shown this
            // size would be enlarged and soft.
            const picture = fileThumbnail(file, 'small')
            return (
              <li key={file.name} className="flex items-center gap-2 text-sm">
                {picture === null ? (
                  <Paperclip className="size-4 shrink-0 text-muted-foreground" />
                ) : (
                  // Decorative: the name is written beside it.
                  <img
                    src={picture}
                    alt=""
                    className="size-8 shrink-0 rounded object-cover"
                  />
                )}
                <span className="truncate">{fileLabel(file)}</span>
                <button
                  type="button"
                  aria-label={t('file.remove', { name: fileLabel(file) })}
                  onClick={() => {
                    onValueChange(
                      JSON.stringify(files.filter((held) => held.name !== file.name)),
                    )
                    // The button is about to be removed, and a focus that goes
                    // nowhere is a keyboard that stops working -- so it moves to
                    // the one control that is always here.
                    picker.current?.focus()
                  }}
                  className="ml-auto rounded p-1 hover:bg-muted"
                >
                  <X className="size-3" />
                </button>
              </li>
            )
          })}
        </ul>
      )}

      <input
        ref={picker}
        // Focused on open, like every other editor. It is not decoration: the
        // editor commits when focus leaves its frame, so an editor that opens
        // with the focus somewhere else commits the moment anything inside it
        // is clicked.
        autoFocus={autoFocus}
        type="file"
        multiple
        aria-label={label}
        onChange={(event) => {
          const chosen = [...(event.target.files ?? [])]
          // Cleared so that choosing the same file twice in a row is two
          // changes rather than one: the input reports no change when the
          // value it holds is the value it already held.
          event.target.value = ''
          void attach(chosen)
        }}
        className={cn(
          'text-sm file:mr-2 file:rounded file:border file:border-input file:bg-background file:px-2 file:py-1 file:text-sm',
        )}
      />

      {uploading > 0 && (
        <p className="text-xs text-muted-foreground">
          {t('file.uploading', { count: uploading })}
        </p>
      )}
      {failed && <p className="text-xs text-destructive">{t('file.failed')}</p>}
    </div>
  )
}
